import React, { useEffect, useState } from 'react';
import { callPublicApi, callApi } from '../api';
import {
    Users, FileText, Calendar, Activity,
    Settings, Shield, Database, Layout,
    Zap, AlertCircle, CheckCircle, ExternalLink,
    TrendingUp, Clock, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import './Analiz.css';

const Analiz = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalApplications: 0,
        pendingApplications: 0,
        totalRevenue: 0,
        totalEvents: 0,
        activeEvents: 0,
        recentApps: []
    });

    const [allUsers, setAllUsers] = useState([]); // Store for export

    const [sysStatus, setSysStatus] = useState({
        api: 'online',
        db: 'online',
        cdn: 'online',
        latency: 0
    });

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadStats = async () => {
            const startTime = Date.now();
            try {
                // Paralel Veri Çekme
                const [usersData, appsData, eventsData] = await Promise.all([
                    callApi('/uyeler', 'GET'),
                    callApi('/basvurular', 'GET'),
                    callApi('/etkinlikler', 'GET')
                ]);

                // Latency Measure
                const endTime = Date.now();
                const latency = endTime - startTime;
                setSysStatus(prev => ({ ...prev, latency }));

                // Veri İşleme
                const users = usersData.items || [];
                const apps = appsData.items || [];
                const events = eventsData.items || [];

                setAllUsers(users); // Save for export

                // --- SADECE AKTİF/GELECEK ETKİNLİKLERE AİT BAŞVURULARI FİLTRELE ---
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // Aktif etkinlikleri bul (tarihi geçmemiş olanlar)
                const activeEventIds = events
                    .filter(e => {
                        const eventDate = new Date(e.etkinlikTarihi || e.tarih);
                        eventDate.setHours(0, 0, 0, 0);
                        return eventDate >= today;
                    })
                    .map(e => String(e.etkinlikID || e.id));

                // Sadece aktif etkinliklere ait başvuruları filtrele
                const activeApps = apps.filter(app => {
                    if (!app.etkinlikID) return false;
                    return activeEventIds.includes(String(app.etkinlikID));
                });

                // Ciro Hesaplama (Sadece aktif etkinlikler için)
                let revenue = 0;
                activeApps.forEach(app => {
                    if (app.durum === 'ONAYLANDI') {
                        const event = events.find(e => String(e.id || e.etkinlikID) === String(app.etkinlikID));
                        if (event && event.fiyat) {
                            revenue += Number(event.fiyat);
                        }
                    }
                });

                // Son 5 Başvuru (Sadece aktif etkinliklerden)
                const recentApps = [...activeApps].sort((a, b) => new Date(b.tarih) - new Date(a.tarih)).slice(0, 5);
                const activeEvents = activeEventIds.length;

                setStats({
                    totalUsers: users.length,
                    totalApplications: activeApps.length,
                    pendingApplications: activeApps.filter(app => app.durum === 'BEKLIYOR').length,
                    totalRevenue: revenue,
                    totalEvents: events.length,
                    activeEvents,
                    recentApps
                });

            } catch (err) {
                console.error("Analiz verisi çekilemedi:", err);
                setSysStatus(prev => ({ ...prev, api: 'degraded' }));
            } finally {
                setLoading(false);
            }
        };

        loadStats();
    }, []);

    const exportToExcel = (e) => {
        e.stopPropagation(); // Card click olmasın
        if (!allUsers || allUsers.length === 0) {
            alert("İndirilecek veri yok veya yükleniyor...");
            return;
        }

        const wb = XLSX.utils.book_new();

        // Format Data
        const exportData = allUsers.map(u => ({
            "Ad Soyad": `${u.ad} ${u.soyad}`,
            "Öğrenci No": u.ogrenciNo,
            "Bölüm": u.bolum,
            "Telefon": u.telefon,
            "Kayıt Tarihi": u.kayitTarihi,
            "Uye ID": u.uyeID
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(wb, ws, "Uyeler");
        XLSX.writeFile(wb, "Klugastronomi_Uyeler.xlsx");
    };


    return (
        <div className="analiz-container">

            {/* HEADER SECTION */}
            <div className="header-section">
                <div>
                    <h5 style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        SYSTEM OVERVIEW
                    </h5>
                    <h1 style={{ margin: '0.5rem 0 0 0', fontSize: '2.2rem', fontWeight: '800', color: '#111827', letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                        <Layout size={36} color="#2563eb" />
                        Komuta Merkezi
                    </h1>
                </div>

                {/* SYSTEM STATUS PILLS */}
                <div className="system-status">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#059669' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#059669', boxShadow: '0 0 10px #059669' }}></div>
                        API OK
                    </div>
                    <div style={{ width: '1px', background: '#e5e7eb' }}></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: 'bold', color: '#059669' }}>
                        <Database size={14} /> DB OK
                    </div>
                    <div style={{ width: '1px', background: '#e5e7eb' }}></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: 'bold', color: sysStatus.latency < 200 ? '#2563eb' : '#f59e0b' }}>
                        <Zap size={14} fill={sysStatus.latency < 200 ? '#2563eb' : '#f59e0b'} /> {sysStatus.latency}ms
                    </div>
                </div>
            </div>

            {/* TOP STATS ROW */}
            <div className="stats-grid">

                {/* Total Users */}
                <div className="stat-card" onClick={() => navigate('/uyeler')}>
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: '#2563eb' }}></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <div>
                            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem', fontWeight: '600' }}>Toplam Üye</p>
                            <h2 style={{ margin: '0.5rem 0 0 0', fontSize: '2.5rem', fontWeight: '800', color: '#111827' }}>
                                {loading ? '...' : stats.totalUsers}
                            </h2>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {/* EXPORT BUTTON */}
                            <button onClick={exportToExcel} title="Excele İndir" style={{
                                background: '#dbeafe', border: 'none', borderRadius: '8px', padding: '8px', cursor: 'pointer', color: '#1e40af'
                            }}>
                                <Download size={20} />
                            </button>
                            <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '12px' }}>
                                <Users size={24} color="#2563eb" />
                            </div>
                        </div>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: '600' }}>
                        <TrendingUp size={14} /> +%12 bu ay
                    </div>
                </div>

                {/* Applications */}
                <div className="stat-card" onClick={() => navigate('/basvurular')}>
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: '#f59e0b' }}></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <div>
                            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem', fontWeight: '600' }}>Başvurular</p>
                            <h2 style={{ margin: '0.5rem 0 0 0', fontSize: '2.5rem', fontWeight: '800', color: '#111827' }}>
                                {loading ? '...' : stats.totalApplications}
                            </h2>
                        </div>
                        <div style={{ background: '#fffbeb', padding: '10px', borderRadius: '12px' }}>
                            <FileText size={24} color="#d97706" />
                        </div>
                    </div>
                    {stats.pendingApplications > 0 ? (
                        <div style={{ fontSize: '0.85rem', color: '#d97706', display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: '700' }}>
                            <AlertCircle size={14} /> {stats.pendingApplications} Bekleyen
                        </div>
                    ) : (
                        <div style={{ fontSize: '0.85rem', color: '#059669', display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: '600' }}>
                            <CheckCircle size={14} /> Hepsi Tamam
                        </div>
                    )}
                </div>

                {/* Revenue */}
                <div className="stat-card" onClick={() => navigate('/analiz')}>
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: '#10b981' }}></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <div>
                            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem', fontWeight: '600' }}>Tahmini Hasılat</p>
                            <h2 style={{ margin: '0.5rem 0 0 0', fontSize: '2.5rem', fontWeight: '800', color: '#111827' }}>
                                {loading ? '...' : stats.totalRevenue.toLocaleString('tr-TR')} ₺
                            </h2>
                        </div>
                        <div style={{ background: '#ecfdf5', padding: '10px', borderRadius: '12px' }}>
                            <Activity size={24} color="#059669" />
                        </div>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500' }}>
                        Aktif Etkinliklerden
                    </div>
                </div>

                {/* Events */}
                <div className="stat-card" onClick={() => navigate('/etkinlikler')}>
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: '#8b5cf6' }}></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                        <div>
                            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem', fontWeight: '600' }}>Aktif Etkinlik</p>
                            <h2 style={{ margin: '0.5rem 0 0 0', fontSize: '2.5rem', fontWeight: '800', color: '#111827' }}>
                                {loading ? '...' : stats.activeEvents}
                            </h2>
                        </div>
                        <div style={{ background: '#f5f3ff', padding: '10px', borderRadius: '12px' }}>
                            <Calendar size={24} color="#7c3aed" />
                        </div>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500' }}>
                        Toplam: {stats.totalEvents}
                    </div>
                </div>
            </div>

            {/* GOOGLE ANALYTICS SECTION */}
            <div className="ga-section" style={{ marginTop: '2rem', background: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <div style={{ padding: '1.5rem', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ background: '#fef3c7', padding: '10px', borderRadius: '12px' }}>
                            <TrendingUp size={24} color="#d97706" />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#374151' }}>Site Trafiği</h3>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#9ca3af' }}>Google Analytics Verileri</p>
                        </div>
                    </div>
                    <a
                        href="https://analytics.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.6rem 1rem', background: '#f3f4f6', borderRadius: '8px',
                            color: '#4b5563', textDecoration: 'none', fontWeight: '600', fontSize: '0.85rem'
                        }}
                    >
                        <ExternalLink size={14} /> Google Analytics'e Git
                    </a>
                </div>

                {/* EMBED AREA */}
                <div style={{ padding: '0', height: '600px', background: '#f9fafb' }}>
                    <iframe
                        width="100%"
                        height="100%"
                        src="https://lookerstudio.google.com/embed/reporting/9849ff6c-4233-45d7-96d7-13e64a7ff41c/page/kIV1C"
                        frameBorder="0"
                        style={{ border: 0 }}
                        allowFullScreen
                        sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                        title="Site Trafik Raporu"
                    ></iframe>
                </div>
            </div>

            <div className="bottom-grid">

                {/* QUICK ACTIONS */}
                <div>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: '700', marginBottom: '1.5rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Zap size={20} color="#f59e0b" fill="#f59e0b" /> Hızlı İşlemler
                    </h3>
                    <div className="quick-actions-grid">
                        <div className="action-btn" onClick={() => navigate('/etkinlikler')}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2563eb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                            <div style={{ background: '#eff6ff', padding: '12px', borderRadius: '50%' }}><Calendar size={24} color="#2563eb" /></div>
                            <span style={{ fontWeight: '600', color: '#1f2937' }}>Etkinlik Ekle/Düzenle</span>
                        </div>

                        <div className="action-btn" onClick={() => navigate('/tarifler')}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#10b981'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                            <div style={{ background: '#ecfdf5', padding: '12px', borderRadius: '50%' }}><FileText size={24} color="#059669" /></div>
                            <span style={{ fontWeight: '600', color: '#1f2937' }}>Tarif Yönetimi</span>
                        </div>

                        <div className="action-btn" onClick={() => navigate('/basvurular')}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                            <div style={{ background: '#fffbeb', padding: '12px', borderRadius: '50%' }}><Users size={24} color="#d97706" /></div>
                            <span style={{ fontWeight: '600', color: '#1f2937' }}>Başvuru Onayla</span>
                        </div>

                        <div className="action-btn" onClick={() => navigate('/site-yonetim')}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                            <div style={{ background: '#f5f3ff', padding: '12px', borderRadius: '50%' }}><Settings size={24} color="#7c3aed" /></div>
                            <span style={{ fontWeight: '600', color: '#1f2937' }}>Site Ayarları</span>
                        </div>
                    </div>

                    {/* MAINTENANCE MODE TEASER */}
                    <div style={{ marginTop: '1.5rem', background: '#252f3f', padding: '1.5rem', borderRadius: '16px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>Sistem Durumu</h4>
                            <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.9rem', color: '#9ca3af' }}>Tüm servisler aktif çalışıyor.</p>
                        </div>
                        <div style={{ background: 'rgba(255,255,255,0.1)', padding: '0.8rem', borderRadius: '50%' }}>
                            <Shield size={28} color="#34d399" />
                        </div>
                    </div>
                </div>

                {/* RECENT ACTIVITY */}
                <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                    <div style={{ padding: '1.5rem', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#374151' }}>Son Başvurular</h3>
                        <div style={{ padding: '0.4rem 0.8rem', background: '#f3f4f6', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '600', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <Clock size={12} /> Canlı
                        </div>
                    </div>

                    <div style={{ padding: '0 1.5rem' }}>
                        {stats.recentApps.map((app, i) => (
                            <div key={i} style={{ padding: '1rem 0', borderBottom: i < stats.recentApps.length - 1 ? '1px solid #f3f4f6' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                    <div style={{
                                        width: '40px', height: '40px', borderRadius: '50%', color: 'white', fontWeight: 'bold', fontSize: '1.1rem',
                                        background: `hsl(${Math.random() * 360}, 70%, 50%)`, // Random Avatar Color
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        {app.adSoyad.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '600', color: '#1f2937' }}>{app.adSoyad}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{app.etkinlikBaslik}</div>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{
                                        padding: '0.3rem 0.8rem', borderRadius: '99px', fontSize: '0.8rem', fontWeight: '700',
                                        background: app.durum === 'ONAYLANDI' ? '#ecfdf5' : (app.durum === 'REDDEDILDI' ? '#fef2f2' : '#fffbeb'),
                                        color: app.durum === 'ONAYLANDI' ? '#059669' : (app.durum === 'REDDEDILDI' ? '#dc2626' : '#d97706')
                                    }}>
                                        {app.durum}
                                    </span>
                                    <div style={{ fontSize: '0.75rem', marginTop: '0.4rem', color: '#9ca3af' }}>
                                        {new Date(app.tarih).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {stats.recentApps.length === 0 && (
                            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>Henüz kayıt bulunmuyor.</div>
                        )}
                    </div>
                    {stats.recentApps.length > 0 && (
                        <div style={{ padding: '1rem', background: '#f9fafb', textAlign: 'center', borderTop: '1px solid #e5e7eb' }}>
                            <button onClick={() => navigate('/basvurular')} style={{ background: 'none', border: 'none', color: '#2563eb', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%' }}>
                                Tümünü Gör <ExternalLink size={16} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Analiz;
